import { DateRange } from "../utils/types";
import {
  BrandHealthGqvQueryInfo,
  BrandHealthTableInfo,
  BrandHealthGqvTimeSeries,
  BrandHealthGqvTimeSeriesObject,
} from "@blisspointmedia/bpm-types/dist/BrandHealthMetrics";
import { getGroupingValue } from "../utils/date-utils";
import { CompanyFiscalCal } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { DateGroupingKey } from "../TVADCrossChannel/homePageConstants";

export const formatGqvQueryDataForTimeSeries = (
  filteredGqvDataByRegionAndDateRange: BrandHealthGqvQueryInfo[]
): BrandHealthGqvTimeSeriesObject => {
  const sumsByDate: { [date: string]: { [label: string]: number } } = {};

  const uniqueLabelsSet = new Set<string>();

  for (let i = 0; i < filteredGqvDataByRegionAndDateRange.length; i++) {
    const row = filteredGqvDataByRegionAndDateRange[i];
    const date = row.ReportDate;
    const label = row.QueryLabel;
    const volume = Number(row.IndexedQueryVolume);

    uniqueLabelsSet.add(label);

    if (!sumsByDate[date]) {
      sumsByDate[date] = {};
    }
    if (!sumsByDate[date][label]) {
      sumsByDate[date][label] = 0;
    }
    sumsByDate[date][label] += volume;
  }

  const uniqueLabels = Array.from(uniqueLabelsSet).sort();

  const allDates = Object.keys(sumsByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  const result: BrandHealthGqvTimeSeries[] = [];

  for (let i = 0; i < allDates.length; i++) {
    const date = allDates[i];
    const dateSums = sumsByDate[date];
    const obj: any = {
      ReportDate: date,
    };

    for (let j = 0; j < uniqueLabels.length; j++) {
      const label = uniqueLabels[j];
      obj[label] = dateSums[label] || 0;
    }

    result.push(obj);
  }
  return { res: result, uniqueLabels: uniqueLabels };
};

export const filterGqvQueryDataByRegionAndDateRange = (
  gqvQueryDataWithGeoNamesCodesAndPopulation: BrandHealthGqvQueryInfo[],
  filters: Partial<Record<string, boolean>>,
  dateRange: DateRange
): BrandHealthGqvQueryInfo[] => {
  const selectedRegions = new Set<string>();
  const hasRegionAll = filters["Region-All"] === true;

  Object.entries(filters).forEach(([region, isSelected]) => {
    if (isSelected) {
      selectedRegions.add(region);
    }
  });

  const startDate = dateRange.start;
  const endDate = dateRange.end;

  return gqvQueryDataWithGeoNamesCodesAndPopulation.filter(item => {
    if (item.QueryLabel === "Brand Affinity - Amazon") {
      return false;
    }

    const reportDate = item.ReportDate;
    if (reportDate < startDate || reportDate > endDate) {
      return false;
    }

    if (hasRegionAll) {
      return true;
    }

    return selectedRegions.has(item.Region);
  });
};

const capitalizeDmaFullName = (input: string): string => {
  return input
    .split(", ")
    .map((segment, index) => {
      if (index === 0) {
        return segment
          .split(" ")
          .map(word =>
            word
              .split("-")
              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join("-")
          )
          .join(" ");
      } else {
        return segment.toUpperCase();
      }
    })
    .join(", ");
};

export const groupGqvQueryDataByDmaCode = (
  data: BrandHealthGqvQueryInfo[]
): BrandHealthTableInfo[] => {
  const aggregation: { [key: string]: BrandHealthTableInfo } = {};

  data.forEach(item => {
    const { dma_code } = item;
    if (!dma_code) {
      return;
    }

    if (!aggregation[dma_code]) {
      aggregation[dma_code] = {
        dma_full_name: capitalizeDmaFullName(item.dma_full_name),
        dma_code: dma_code,
        "Absolute Index": 0,
        Population: parseFloat(item.population),
      };
    }

    const volume =
      typeof item.IndexedQueryVolume === "number"
        ? item.IndexedQueryVolume
        : parseFloat(item.IndexedQueryVolume);

    aggregation[dma_code]["Absolute Index"] += volume;
  });
  const groupGqvQueryDataByDmaCode = Object.values(aggregation);
  const avgIndexPerPopulation =
    groupGqvQueryDataByDmaCode.reduce(
      (sum, obj) => sum + obj["Absolute Index"] / obj.Population,
      0
    ) / groupGqvQueryDataByDmaCode.length;
  const adjustedData = groupGqvQueryDataByDmaCode.map(obj => ({
    ...obj,
    "Absolute Index Adjusted": obj["Absolute Index"] / obj.Population / avgIndexPerPopulation,
  }));
  return adjustedData;
};

export const groupGqvQueryDataByDmaCodeAndQueryLabel = (data: BrandHealthGqvQueryInfo[]): any[] => {
  const aggregation: { [key: string]: { [key: string]: any } } = {};

  data.forEach(item => {
    const { dma_code, QueryLabel, IndexedQueryVolume, dma_full_name, population } = item;

    if (!dma_code || !QueryLabel) {
      return;
    }

    if (!aggregation[dma_code]) {
      aggregation[dma_code] = {
        dma_full_name: capitalizeDmaFullName(dma_full_name),
        dma_code: dma_code,
        Population: parseFloat(population),
      };
    }

    if (!aggregation[dma_code][QueryLabel]) {
      aggregation[dma_code][QueryLabel] = 0;
    }

    const volume =
      typeof IndexedQueryVolume === "number" ? IndexedQueryVolume : parseFloat(IndexedQueryVolume);

    aggregation[dma_code][QueryLabel] += volume;
  });

  return Object.values(aggregation).map(entry => {
    const { dma_full_name, dma_code, Population, ...queryLabelData } = entry;

    return {
      "DMA Full Name": dma_full_name,
      "DMA Code": dma_code,
      Population,
      ...queryLabelData,
    };
  });
};

export const returnTitleForGqvChart = (typeOfChart: string): string => {
  let title = "";
  switch (typeOfChart) {
    case "showGraph":
      title = "Indexed Google Query Volume";
      break;
    case "showTable":
      title = "Total Google Query Volume";
      break;
    case "showMap":
      title = "Indexed Google Query Volume by DMA";
      break;
    default:
      title = "Indexed Google Query Volume";
      break;
  }

  return title;
};

export const filterTableDataByLabel = (
  filteredGqvDataByRegionAndDateRange: BrandHealthGqvQueryInfo[],
  label: string
): BrandHealthGqvQueryInfo[] => {
  const filteredGqvDataByLabel = filteredGqvDataByRegionAndDateRange.filter(
    row => row.QueryLabel === label
  );
  return filteredGqvDataByLabel;
};

export const groupFormattedGqvLineChartDataByDate = (
  formattedGqvLineChartData: BrandHealthGqvTimeSeries[],
  groupingOption: DateGroupingKey,
  fiscalCal: CompanyFiscalCal
): BrandHealthGqvTimeSeries[] => {
  const groups: { [groupKey: string]: BrandHealthGqvTimeSeries } = {};

  formattedGqvLineChartData.forEach((item: BrandHealthGqvTimeSeries) => {
    const groupKey = getGroupingValue(item.ReportDate, groupingOption, fiscalCal);

    if (!groups[groupKey]) {
      groups[groupKey] = { ReportDate: groupKey };
    }

    for (const [key, value] of Object.entries(item)) {
      if (key !== "ReportDate" && typeof value === "number") {
        groups[groupKey][key] = ((groups[groupKey][key] as number) || 0) + value;
      }
    }
  });

  return Object.values(groups);
};

export const formatAndRoundNumber = (num: number | null): string => {
  if (num == null) {
    return "";
  }

  if (num === 0) {
    return "0";
  }

  if (Math.abs(num) < 0.5) {
    return "<1";
  }

  const roundedValue = Math.round(num);

  return roundedValue.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const calculateMovingAverage = (
  groupByDateGqvData: BrandHealthGqvTimeSeries[],
  uniqueLabels: string[],
  movingAvgDaysGqv: string
): BrandHealthGqvTimeSeries[] => {
  if (movingAvgDaysGqv === "1") {
    return groupByDateGqvData;
  }

  const movingAvg = parseInt(movingAvgDaysGqv, 10);
  const sortedData = [...groupByDateGqvData].sort(
    (a, b) => new Date(a.ReportDate).getTime() - new Date(b.ReportDate).getTime()
  );

  return sortedData.map((entry, index) => {
    const newEntry = { ...entry };

    uniqueLabels.forEach(label => {
      const start = Math.max(0, index - movingAvg + 1);
      const values = sortedData
        .slice(start, index + 1)
        .map(item => (typeof item[label] === "number" ? (item[label] as number) : 0));

      const movingAverage =
        values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

      newEntry[label] = movingAverage;
    });

    return newEntry;
  });
};

export const getMedian = (gqvMapData: number[]): number => {
  if (gqvMapData.length === 0) {
    return 0;
  }
  const sortedGqvMapData = gqvMapData.sort((a, b) => a - b);
  const midIndexOfGqvData = Math.floor(sortedGqvMapData.length / 2);
  return midIndexOfGqvData % 2 !== 0
    ? sortedGqvMapData[midIndexOfGqvData]
    : (sortedGqvMapData[midIndexOfGqvData - 1] + sortedGqvMapData[midIndexOfGqvData]) / 2;
};

export const getStandardDeviation = (gqvMapData: number[]): number => {
  if (gqvMapData.length === 0) {
    return 0;
  }
  const mean = gqvMapData.reduce((sum, val) => sum + val, 0) / gqvMapData.length;
  const variance = gqvMapData.reduce((acc, val) => acc + (val - mean) ** 2, 0) / gqvMapData.length;
  return Math.sqrt(variance);
};

const getThirdThresholds = (start: number, end: number): number[] => {
  const step = (end - start) / 3;
  return [start + step, start + 2 * step];
};

export const getThresholdBinsWithStdDevNonAdjusted = (gqvMapData: number[]): number[] => {
  const minIndex = Math.min(...gqvMapData);
  const maxIndex = Math.max(...gqvMapData);
  const median = getMedian(gqvMapData);
  const stdDev = getStandardDeviation(gqvMapData);
  const adjustedCeiling = median + 2.5 * stdDev;
  const adjustedMaxIndex = Math.min(maxIndex, adjustedCeiling);

  return [
    minIndex,
    ...getThirdThresholds(minIndex, median),
    median,
    ...getThirdThresholds(median, adjustedMaxIndex),
    adjustedMaxIndex,
  ];
};

const getQuarterThresholds = (start: number, end: number): number[] => {
  const step = (end - start) / 4;
  return [start + step, start + 2 * step, start + 3 * step];
};

export const getThresholdBinsWithStdDev = (gqvMapData: number[]): number[] => {
  const minIndex = Math.min(...gqvMapData);
  const maxIndex = Math.max(...gqvMapData);
  const median = getMedian(gqvMapData);
  const stdDev = getStandardDeviation(gqvMapData);
  const adjustedCeiling = median + 2.5 * stdDev;
  const adjustedMaxIndex = Math.min(maxIndex, adjustedCeiling);

  return [
    minIndex,
    ...getQuarterThresholds(minIndex, 1),
    1,
    ...getQuarterThresholds(1, adjustedMaxIndex),
    adjustedMaxIndex,
  ];
};

export const getColorForIndexedVolume = (
  indexedVolume: number,
  thresholds: number[],
  mapColors: string[]
): string => {
  for (let i = 0; i < thresholds.length - 1; i++) {
    if (indexedVolume >= thresholds[i] && indexedVolume < thresholds[i + 1]) {
      return mapColors[i];
    }
  }
  return mapColors[mapColors.length - 1];
};
